import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { EmailStatusChip, RunStatusChip, REJECT_REASONS, fmtDateTime, btn, Spinner } from './agentUi';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../utils/apiClient';

interface Props {
  emailId: string;
  onClose: () => void;
  onOpenRun: (runId: string) => void;
  onChanged: () => void;
}

type DetailResponse = Awaited<ReturnType<typeof agentApi.inboxDetail>>;

/** Right-hand drawer: headers, verdicts, body, attachments as document chips, re-run / assign. */
const InboundEmailDetail: React.FC<Props> = ({ emailId, onClose, onOpenRun, onChanged }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState<Array<{ id: string; email: string; firstName?: string; lastName?: string }>>([]);
  const [assignTo, setAssignTo] = useState('');
  const isAdmin = user?.role === 'admin' || user?.role === 'system_admin';

  useEffect(() => {
    agentApi.inboxDetail(emailId).then(setData).catch((err) => setError(apiErrorMessage(err, 'Could not load the email')));
  }, [emailId]);

  useEffect(() => {
    if (!isAdmin || !data || !['quarantined', 'rejected', 'failed'].includes(data.email.status)) return;
    apiClient
      .get<any>('/api/admin/users')
      .then((r) => setUsers(Array.isArray(r) ? r : r?.users || []))
      .catch(() => setUsers([]));
  }, [isAdmin, data]);

  const rerun = async () => {
    setBusy(true);
    try {
      const { runId } = await agentApi.rerun(emailId);
      onChanged();
      onOpenRun(runId);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not re-run'));
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!assignTo) return;
    setBusy(true);
    try {
      const { runId } = await agentApi.assign(emailId, assignTo);
      onChanged();
      onOpenRun(runId);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not assign'));
    } finally {
      setBusy(false);
    }
  };

  const e = data?.email;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="w-full max-w-xl h-full overflow-y-auto bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-xl p-5" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white break-words">{e?.subject || 'Email'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">✕</button>
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}
        {!data ? (
          <div className="flex items-center gap-2 text-gray-500"><Spinner /> Loading…</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <EmailStatusChip status={e!.status} />
              {e!.rejectReason && <span className="text-xs text-gray-600 dark:text-gray-300">{REJECT_REASONS[e!.rejectReason] || e!.rejectReason}</span>}
              {e!.simulated && <span className="text-xs text-purple-600">simulated</span>}
            </div>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-gray-500">From</dt>
              <dd className="text-gray-900 dark:text-gray-100">{e!.fromName ? `${e!.fromName} <${e!.fromAddress}>` : e!.fromAddress}</dd>
              <dt className="text-gray-500">To</dt>
              <dd className="text-gray-900 dark:text-gray-100 break-words">{(e!.toAddresses || []).join(', ')}</dd>
              {(e!.ccAddresses || []).length > 0 && (
                <>
                  <dt className="text-gray-500">Cc</dt>
                  <dd className="text-gray-900 dark:text-gray-100 break-words">{(e!.ccAddresses || []).join(', ')}</dd>
                </>
              )}
              <dt className="text-gray-500">Received</dt>
              <dd className="text-gray-900 dark:text-gray-100">{fmtDateTime(e!.receivedAt)}</dd>
              {e!.plusTag && (
                <>
                  <dt className="text-gray-500">Pinned</dt>
                  <dd className="text-gray-900 dark:text-gray-100">+{e!.plusTag}{e!.pinnedIntent ? ` (${e!.pinnedIntent.replace(/_/g, ' ')})` : ''}{e!.pinnedCustomerId ? ' · customer matched' : ''}</dd>
                </>
              )}
              {e!.verdicts && Object.keys(e!.verdicts).length > 0 && (
                <>
                  <dt className="text-gray-500">Authenticity</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{String(e!.verdicts.detail || e!.verdicts.basis || '')}</dd>
                </>
              )}
            </dl>

            {data.documents.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Documents</h3>
                <div className="flex flex-wrap gap-1.5">
                  {data.documents.map((d) => (
                    <button
                      key={d.id}
                      disabled={!d.customerId || !d.projectId}
                      onClick={() => navigate(`/customers/${d.customerId}/projects/${d.projectId}?tab=documents`)}
                      className="text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-70"
                      title={d.isStaged ? 'Not filed yet (staged)' : 'Open the folder'}
                    >
                      📄 {d.name}{d.isStaged ? ' · unfiled' : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(e!.attachments || []).filter((a) => !a.documentId).length > 0 && (
              <div className="text-xs text-gray-500">
                Not staged: {(e!.attachments || []).filter((a) => !a.documentId).map((a) => `${a.filename} (${a.skipped})`).join('; ')}
              </div>
            )}
            {e!.forwardedOriginal && (
              <div className="rounded border border-gray-200 dark:border-gray-700 p-2 text-xs">
                <div className="font-medium text-gray-700 dark:text-gray-200">
                  {e!.forwardedOriginal.more?.length ? `${e!.forwardedOriginal.more.length + 1} emails attached` : 'Forwarded email attached'}
                </div>
                {[e!.forwardedOriginal, ...(e!.forwardedOriginal.more || [])].map((f, i) => (
                  <div key={i} className="text-gray-500 dark:text-gray-400">From {f.from} — “{f.subject}”</div>
                ))}
              </div>
            )}

            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Body</h3>
              <pre className="whitespace-pre-wrap break-words text-xs bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded p-3 max-h-80 overflow-auto">{e!.bodyText || '(empty)'}</pre>
            </div>

            {data.runs.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Runs</h3>
                <ul className="space-y-1">
                  {data.runs.map((r) => (
                    <li key={r.id}>
                      <button className="text-left hover:underline flex items-center gap-2" onClick={() => onOpenRun(r.id)}>
                        <RunStatusChip status={r.status} mode={r.mode} /> <span className="text-xs text-gray-500">{fmtDateTime(r.createdAt)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              {e!.resolvedUserId && ['completed', 'failed'].includes(e!.status) && (
                <button className={btn.secondary} onClick={rerun} disabled={busy}>Re-run</button>
              )}
              {isAdmin && ['quarantined', 'rejected', 'failed'].includes(e!.status) && (
                <div className="flex gap-2 items-center w-full">
                  <select className="flex-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5" value={assignTo} onChange={(ev) => setAssignTo(ev.target.value)}>
                    <option value="">Assign to a user…</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} ({u.email})</option>
                    ))}
                  </select>
                  <button className={btn.primary} onClick={assign} disabled={busy || !assignTo}>Assign &amp; run</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboundEmailDetail;
